import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, FileText, Loader2, X } from 'lucide-react';
import { useModalAccessibility } from '../hooks/useModalAccessibility';

interface Props {
  name: string;
  isUser: boolean;
  isUploading?: boolean;
  onDownload?: () => Promise<void>;
}

const ChatDocumentAttachment: React.FC<Props> = ({ name, isUser, isUploading, onDownload }) => {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useModalAccessibility(dialogRef, { enabled: open, onClose: () => setOpen(false), initialFocusRef: closeRef });
  const extension = name.match(/(\.[^.\s]{1,8})$/)?.[1] || '';
  const download = async () => {
    if (!onDownload || downloading) return;
    setDownloading(true);
    setError(false);
    try { await onDownload(); } catch { setError(true); } finally { setDownloading(false); }
  };
  return <>
    <button type="button" onClick={() => { setError(false); setOpen(true); }} title={name}
      aria-label={`Abrir documento ${name}`}
      className={`mb-2 flex w-fit max-w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris ${isUser ? 'border-line/20 bg-panel/10 text-white hover:bg-panel/20' : 'border-warning bg-warning-soft text-warning hover:bg-warning-soft'}`}>
      <FileText size={17} className="shrink-0" aria-hidden="true" />
      <span className="flex min-w-0 max-w-[20rem] font-semibold"><span className="min-w-0 truncate">{extension ? name.slice(0, -extension.length) : name}</span><span className="shrink-0">{extension}</span></span>
      {isUploading && <Loader2 size={16} className="shrink-0 animate-spin" aria-label="Subiendo documento" />}
    </button>
    {open && typeof document !== 'undefined' && createPortal(
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-canvas/80 p-4" onMouseDown={event => { if (event.target === event.currentTarget) setOpen(false); }}>
        <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`Documento adjunto: ${name}`} tabIndex={-1} className="relative w-full max-w-md rounded-2xl bg-panel p-6 shadow-xl outline-none">
          <button ref={closeRef} type="button" onClick={() => setOpen(false)} aria-label="Cerrar documento adjunto" className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full text-muted hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris"><X size={22} aria-hidden="true" /></button>
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-juris-soft text-juris"><FileText size={28} aria-hidden="true" /></div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-juris">Documento adjunto</p>
          <p className="mb-5 break-all font-semibold text-ink">{name}</p>
          <button type="button" onClick={() => void download()} disabled={!onDownload || isUploading || downloading} className="flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-action px-4 py-3 font-semibold text-white hover:bg-action-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2 disabled:opacity-50">{downloading ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Download size={18} aria-hidden="true" />}Descargar original</button>
          {isUploading && <p className="mt-3 text-sm text-muted">La descarga estará disponible cuando termine la subida.</p>}
          {!onDownload && !isUploading && <p className="mt-3 text-sm text-muted">Este documento no está disponible para descargar.</p>}
          {error && <p role="alert" className="mt-3 text-sm text-danger">No se pudo descargar el documento. Inténtalo de nuevo.</p>}
        </div>
      </div>, document.body)}
  </>;
};

export default ChatDocumentAttachment;
